import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PlusIcon, CubeTransparentIcon, PencilIcon, TrashIcon, XMarkIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [recipes, setRecipes] = useState([]);
  const [recipeDetails, setRecipeDetails] = useState({}); // Cache per i dettagli completi delle ricette
  const [packagingComponents, setPackagingComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null); // Track quale prodotto è espanso
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    recipe_id: '',
    bottle_volume_ml: '750',
    commercial_price_excl_excise: '',
    public_price_incl_vat_excise: '',
    average_selling_price: '',
    notes: ''
  });
  const [productPackaging, setProductPackaging] = useState([
    { component_id: '', quantity: '1' }
  ]);

  useEffect(() => {
    loadProducts();
    loadRecipes();
    loadPackagingComponents();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Errore nel caricamento dei prodotti:', error);
      toast.error('Errore nel caricamento dei prodotti');
    } finally {
      setLoading(false);
    }
  };

  const loadRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes`);
      setRecipes(response.data);
    } catch (error) {
      console.error('Errore nel caricamento delle ricette:', error);
      toast.error('Errore nel caricamento delle ricette');
    }
  };

  const loadRecipeDetails = async (recipeId) => {
    // Se già in cache, non ricaricare
    if (recipeDetails[recipeId]) {
      return recipeDetails[recipeId];
    }
    
    try {
      const response = await axios.get(`${API}/recipes/${recipeId}`);
      setRecipeDetails(prev => ({
        ...prev,
        [recipeId]: response.data
      }));
      return response.data;
    } catch (error) {
      console.error('Errore nel caricamento dettagli ricetta:', error);
      return null;
    }
  };

  // Carica i dettagli delle ricette per tutti i prodotti
  useEffect(() => {
    if (products.length > 0) {
      products.forEach(product => {
        if (product.recipe_id && !recipeDetails[product.recipe_id]) {
          loadRecipeDetails(product.recipe_id);
        }
      });
    }
  }, [products]);

  const loadPackagingComponents = async () => {
    try {
      const response = await axios.get(`${API}/packaging`);
      setPackagingComponents(response.data);
    } catch (error) {
      console.error('Errore nel caricamento del packaging:', error);
      toast.error('Errore nel caricamento del packaging');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const packaging = productPackaging
        .filter(comp => comp.component_id && comp.quantity)
        .map(comp => ({
          component_id: comp.component_id,
          quantity: parseFloat(comp.quantity)
        }));

      if (packaging.length === 0) {
        toast.error('Aggiungi almeno un componente di packaging');
        return;
      }

      const payload = {
        ...formData,
        bottle_volume_ml: parseFloat(formData.bottle_volume_ml),
        commercial_price_excl_excise: parseFloat(formData.commercial_price_excl_excise) || 0,
        public_price_incl_vat_excise: parseFloat(formData.public_price_incl_vat_excise) || 0,
        average_selling_price: parseFloat(formData.average_selling_price) || 0,
        packaging_components: packaging
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload);
        toast.success('Prodotto aggiornato con successo');
      } else {
        await axios.post(`${API}/products`, payload);
        toast.success('Prodotto creato con successo');
      }

      await loadProducts();
      resetForm();
      setIsModalOpen(false);
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      toast.error('Errore nel salvataggio del prodotto');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      recipe_id: '',
      bottle_volume_ml: '750',
      commercial_price_excl_excise: '',
      public_price_incl_vat_excise: '',
      average_selling_price: '',
      notes: ''
    });
    setProductPackaging([{ component_id: '', quantity: '1' }]);
    setEditingProduct(null);
  };

  const addPackagingComponent = () => {
    setProductPackaging([...productPackaging, { component_id: '', quantity: '1' }]);
  };

  const removePackagingComponent = (index) => {
    const components = productPackaging.filter((_, i) => i !== index);
    setProductPackaging(components);
  };

  const updatePackagingComponent = (index, field, value) => {
    const components = [...productPackaging];
    components[index][field] = value;
    setProductPackaging(components);
  };

  const getRecipeName = (recipeId) => {
    const recipe = recipes.find(rec => rec.id === recipeId);
    return recipe ? recipe.name : 'Ricetta non trovata';
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      code: product.code,
      recipe_id: product.recipe_id,
      bottle_volume_ml: product.bottle_volume_ml.toString(),
      commercial_price_excl_excise: product.commercial_price_excl_excise.toString(),
      public_price_incl_vat_excise: product.public_price_incl_vat_excise.toString(),
      average_selling_price: product.average_selling_price.toString(),
      notes: product.notes || ''
    });
    
    // Set packaging components
    const packaging = product.packaging_components.map(comp => ({
      component_id: comp.component_id,
      quantity: comp.quantity.toString()
    }));
    setProductPackaging(packaging);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) {
      return;
    }

    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success('Prodotto eliminato con successo');
      await loadProducts();
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
      toast.error('Errore nell\'eliminazione del prodotto');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API}/products/${id}/duplicate`);
      toast.success('Prodotto duplicato con successo');
      await loadProducts();
    } catch (error) {
      console.error('Errore nella duplicazione:', error);
      toast.error('Errore nella duplicazione del prodotto');
    }
  };

  const getComponentName = (componentId) => {
    const component = packagingComponents.find(comp => comp.id === componentId);
    return component ? component.name : 'Componente non trovato';
  };

  const getAlcoholDegreeFromRecipe = (recipeId) => {
    const recipe = recipes.find(rec => rec.id === recipeId);
    return recipe ? recipe.target_alcohol_degree : 35; // Default 35° se non trovato
  };

  // Funzione rimossa - ora usiamo i dati salvati nel prodotto

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="products-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestione Prodotti</h1>
          <p className="text-gray-600">Gestisci i prodotti finali combinando ricette e packaging</p>
        </div>
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-product-btn">
              <PlusIcon className="w-4 h-4 mr-2" />
              Nuovo Prodotto
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}
              </DialogTitle>
              <DialogDescription>
                Crea un prodotto combinando una ricetta con componenti di packaging. I calcoli di costo e accise saranno automatici.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome Prodotto *</Label>
                  <Input
                    id="name"
                    data-testid="product-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Es. Amaro del Capo 750ml"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Codice Prodotto *</Label>
                  <Input
                    id="code"
                    data-testid="product-code-input"
                    value={formData.code}
                    onChange={(e) => setFormData({...formData, code: e.target.value})}
                    placeholder="Es. AMR-750-35"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="recipe_id">Ricetta *</Label>
                  <Select
                    value={formData.recipe_id}
                    onValueChange={(value) => setFormData({...formData, recipe_id: value})}
                  >
                    <SelectTrigger data-testid="product-recipe-select">
                      <SelectValue placeholder="Seleziona ricetta" />
                    </SelectTrigger>
                    <SelectContent>
                      {recipes.map((recipe) => (
                        <SelectItem key={recipe.id} value={recipe.id}>
                          {recipe.name} ({recipe.target_alcohol_degree}° - {recipe.final_volume}L)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bottle_volume_ml">Volume Bottiglia (ml) *</Label>
                  <Input
                    id="bottle_volume_ml"
                    data-testid="product-volume-input"
                    type="number"
                    value={formData.bottle_volume_ml}
                    onChange={(e) => setFormData({...formData, bottle_volume_ml: e.target.value})}
                    placeholder="750"
                    required
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-medium text-gray-900">Componenti Packaging</h4>
                  <Button type="button" variant="outline" size="sm" onClick={addPackagingComponent}>
                    <PlusIcon className="w-4 h-4 mr-2" />
                    Aggiungi Componente
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {productPackaging.map((packaging, index) => (
                    <div key={index} className="flex gap-4 items-end">
                      <div className="flex-1">
                        <Label>Componente *</Label>
                        <Select
                          value={packaging.component_id}
                          onValueChange={(value) => updatePackagingComponent(index, 'component_id', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona componente" />
                          </SelectTrigger>
                          <SelectContent>
                            {packagingComponents.map((component) => (
                              <SelectItem key={component.id} value={component.id}>
                                {component.name} ({component.code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-32">
                        <Label>Quantità *</Label>
                        <Input
                          type="number"
                          step="1"
                          value={packaging.quantity}
                          onChange={(e) => updatePackagingComponent(index, 'quantity', e.target.value)}
                          placeholder="1"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => removePackagingComponent(index)}
                        disabled={productPackaging.length === 1}
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-900 mb-3">Listini Prezzi</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="commercial_price_excl_excise">Listino Commerciale (€)</Label>
                    <Input
                      id="commercial_price_excl_excise"
                      data-testid="product-commercial-price-input"
                      type="number"
                      step="0.01"
                      value={formData.commercial_price_excl_excise}
                      onChange={(e) => setFormData({...formData, commercial_price_excl_excise: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="public_price_incl_vat_excise">Listino Pubblico (€)</Label>
                    <Input
                      id="public_price_incl_vat_excise"
                      data-testid="product-public-price-input"
                      type="number"
                      step="0.01"
                      value={formData.public_price_incl_vat_excise}
                      onChange={(e) => setFormData({...formData, public_price_incl_vat_excise: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="average_selling_price">Vendita Media (€)</Label>
                    <Input
                      id="average_selling_price"
                      data-testid="product-average-price-input"
                      type="number"
                      step="0.01"
                      value={formData.average_selling_price}
                      onChange={(e) => setFormData({...formData, average_selling_price: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Note</Label>
                <Input
                  id="notes"
                  data-testid="product-notes-input"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Note aggiuntive sul prodotto"
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsModalOpen(false)}
                  data-testid="cancel-product-btn"
                >
                  Annulla
                </Button>
                <Button type="submit" data-testid="save-product-btn">
                  {editingProduct ? 'Aggiorna' : 'Salva'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Input
              type="text"
              placeholder="🔍 Cerca per nome o codice..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-4"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-sm text-gray-500 mt-2">
              {products.filter(p => 
                p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.code.toLowerCase().includes(searchQuery.toLowerCase())
              ).length} risultati trovati
            </p>
          )}
        </CardContent>
      </Card>

      {/* Products List */}
      {products.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <CubeTransparentIcon className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun prodotto</h3>
            <p className="text-gray-500 text-center mb-6">
              I prodotti finali sono la combinazione di ricette del liquido e componenti di packaging.
              <br />
              Per creare un prodotto, assicurati di aver già definito ricette e componenti packaging.
            </p>
            <div className="space-y-4 text-sm text-gray-600 max-w-2xl">
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-900 mb-2">Un prodotto include:</h4>
                <ul className="space-y-1 text-green-800">
                  <li>• <strong>Ricetta del liquido:</strong> definisce la composizione e i costi degli ingredienti</li>
                  <li>• <strong>Packaging:</strong> bottiglia, tappo, etichette, scatola, ecc.</li>
                  <li>• <strong>Calcoli automatici:</strong> costo industriale, accise, margini</li>
                  <li>• <strong>Listini:</strong> commerciale, pubblico, vendita media</li>
                </ul>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg">
                <h4 className="font-medium text-amber-900 mb-2">Calcolo Accise Automatico:</h4>
                <p className="text-amber-800 text-xs">
                  Per ogni prodotto viene calcolata automaticamente l'accisa in base al contenuto di alcol:
                  <br />
                  <strong>Accisa = Volume Alcol Anidro × 10,3552€</strong>
                  <br />
                  Esempio: Bottiglia 700ml al 35% = 0,245L alcol × 10,3552€ = 2,54€
                </p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <h4 className="font-medium text-red-900 mb-2">Contrassegni di Stato - Calcolo Automatico:</h4>
                <div className="text-red-800 text-xs space-y-1">
                  <p>Il costo del contrassegno viene calcolato automaticamente in base al formato:</p>
                  <div className="grid grid-cols-2 gap-2 mt-2 font-mono text-xs">
                    <div>• 5-10 cl: €0.005</div>
                    <div>• 20 cl: €0.015</div>
                    <div>• 35-100 cl: €0.047</div>
                    <div>• oltre 1L fino a 3L: €0.094</div>
                  </div>
                </div>
              </div>
            </div>
            <Button className="mt-4" onClick={() => setIsModalOpen(true)}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Crea primo prodotto
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {products
            .filter(p => 
              p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              p.code.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map((product) => {
            const isExpanded = expandedProduct === product.id;
            
            return (
              <Card key={product.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <CardTitle className="text-lg">{product.name}</CardTitle>
                        <Badge variant="outline" className="text-xs">{product.code}</Badge>
                      </div>
                      <CardDescription className="text-sm">
                        {product.recipe_name} • {product.bottle_volume_ml}ml
                      </CardDescription>
                    </div>
                    
                    {/* Riepilogo Costi - Sempre Visibile */}
                    <div className="flex items-center space-x-4 ml-4">
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Costo Industriale</div>
                        <div className="text-lg font-bold text-gray-900">{formatCurrency(product.industrial_cost)}</div>
                        <div className="text-xs text-orange-600 mt-1">
                          Accisa + Contrassegno: {formatCurrency((product.excise_alcohol_cost || 0) + (product.state_mark_cost || 0.047))}
                        </div>
                      </div>
                      
                      <div className="flex space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedProduct(isExpanded ? null : product.id)}
                          data-testid={`toggle-details-${product.id}`}
                          className="h-8 w-8 p-0"
                        >
                          {isExpanded ? (
                            <XMarkIcon className="w-5 h-5" />
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                            </svg>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(product)}
                          data-testid={`edit-product-${product.id}`}
                          className="h-8 w-8 p-0"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDuplicate(product.id)}
                          data-testid={`duplicate-product-${product.id}`}
                          className="h-8 w-8 p-0"
                          title="Duplica prodotto"
                        >
                          <DocumentDuplicateIcon className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(product.id)}
                          data-testid={`delete-product-${product.id}`}
                          className="h-8 w-8 p-0"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                {/* Dettagli Espandibili */}
                {isExpanded && (
                  <CardContent className="pt-0 border-t">
                    <div className="space-y-4 mt-4">
                  {/* Scomposizione Costi */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">📊 Scomposizione Costi per Bottiglia:</h4>
                    <div className="space-y-1 text-sm">
                      {(() => {
                        // Usa i valori già calcolati dal backend
                        const liquidCost = product.liquid_cost || 0;
                        const packagingTotal = product.packaging_cost || product.packaging_components.reduce((sum, comp) => sum + comp.cost, 0);
                        const subtotal = liquidCost + packagingTotal;
                        
                        // Usa le accise già calcolate correttamente nel backend
                        const alcoholExcise = product.excise_alcohol_cost || 0;
                        const infusionExcise = product.excise_infusions_cost || 0;
                        const stateMark = product.state_mark_cost || 0.047;
                        
                        return (
                          <>
                            <div className="flex justify-between text-blue-700">
                              <span>• Costo Ricetta ({product.bottle_volume_ml}ml):</span>
                              <span className="font-medium">{formatCurrency(liquidCost)}</span>
                            </div>
                            <div className="flex justify-between text-green-700">
                              <span>• Costo Packaging:</span>
                              <span className="font-medium">{formatCurrency(packagingTotal)}</span>
                            </div>
                            <div className="flex justify-between text-gray-700 border-t pt-1 font-medium">
                              <span>Subtotale (Ricetta + Packaging):</span>
                              <span>{formatCurrency(subtotal)}</span>
                            </div>
                            <div className="flex justify-between text-orange-700">
                              <span>• Accisa da Alcol (prodotto finale):</span>
                              <span className="font-medium">{formatCurrency(alcoholExcise)}</span>
                            </div>
                            <div className="flex justify-between text-orange-600">
                              <span>• Accisa da Infusi/Aromi:</span>
                              <span className="font-medium">{formatCurrency(infusionExcise)}</span>
                            </div>
                            <div className="flex justify-between text-red-700">
                              <span>• Contrassegno di Stato ({product.bottle_volume_ml}ml):</span>
                              <span className="font-medium">{formatCurrency(stateMark)}</span>
                            </div>
                            <div className="flex justify-between text-gray-900 border-t pt-1 font-bold bg-white px-2 py-1 rounded">
                              <span>💰 COSTO INDUSTRIALE TOTALE:</span>
                              <span>{formatCurrency(product.industrial_cost)}</span>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Dettaglio Ricetta e Ingredienti */}
                  {(() => {
                    const recipe = recipeDetails[product.recipe_id];
                    if (!recipe || !recipe.components) return null;
                    
                    const bottlesFromRecipe = product.excise_details?.bottles_from_recipe || 1;
                    
                    return (
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <h4 className="text-sm font-medium text-blue-900 mb-2">🧪 Dettaglio Ricetta per Bottiglia ({product.bottle_volume_ml}ml):</h4>
                        <div className="space-y-2 text-xs">
                          {recipe.components.map((component, index) => {
                            const costPerBottle = component.cost / bottlesFromRecipe;
                            const alcoholAnhydrousPerBottle = component.alcohol_content_anhydrous / bottlesFromRecipe;
                            const quantityPerBottle = component.quantity / bottlesFromRecipe;
                            
                            // Determina il tipo di ingrediente
                            let ingredientType = '';
                            let borderColor = '#94a3b8'; // grigio default
                            let bgColor = '#f1f5f9';
                            let icon = '📦';
                            
                            const ingredientName = component.ingredient_name.toLowerCase();
                            if (ingredientName.includes('alcool') && component.alcohol_percentage >= 96) {
                              ingredientType = 'Alcol Puro';
                              borderColor = '#f97316';
                              bgColor = '#fed7aa';
                              icon = '🔥';
                            } else if (component.alcohol_percentage > 0) {
                              ingredientType = 'Infuso/Aroma';
                              borderColor = '#fb923c';
                              bgColor = '#fef3c7';
                              icon = '🌿';
                            } else if (ingredientName.includes('zucchero') || ingredientName.includes('sugar')) {
                              ingredientType = 'Zucchero';
                              borderColor = '#ec4899';
                              bgColor = '#fce7f3';
                              icon = '🍬';
                            } else if (ingredientName.includes('acqua') || ingredientName.includes('water')) {
                              ingredientType = 'Acqua';
                              borderColor = '#3b82f6';
                              bgColor = '#dbeafe';
                              icon = '💧';
                            } else {
                              ingredientType = 'Altro';
                              icon = '📦';
                            }
                            
                            return (
                              <div key={index} className="bg-white px-3 py-2 rounded border-l-4" style={{borderLeftColor: borderColor}}>
                                <div className="flex justify-between items-start mb-1">
                                  <span className="font-medium text-blue-900">
                                    {component.ingredient_name} ({component.ingredient_code})
                                  </span>
                                  <span className="text-xs px-2 py-0.5 rounded" style={{backgroundColor: bgColor}}>
                                    {icon} {ingredientType}
                                  </span>
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-blue-700">
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Quantità per bottiglia:</span>
                                    <span className="font-medium">{quantityPerBottle.toFixed(3)} {component.unit_of_measure}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-600">Costo per bottiglia:</span>
                                    <span className="font-medium">{formatCurrency(costPerBottle)}</span>
                                  </div>
                                  {component.alcohol_percentage > 0 && (
                                    <>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Grado alcolico:</span>
                                        <span className="font-medium">{component.alcohol_percentage.toFixed(1)}%</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Alcol anidro:</span>
                                        <span className="font-medium">{(alcoholAnhydrousPerBottle * 1000).toFixed(1)}ml</span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-3 pt-3 border-t border-blue-200 space-y-1">
                          <div className="flex justify-between text-sm text-blue-900 font-medium">
                            <span>Costo Ricetta Totale per bottiglia:</span>
                            <span>{formatCurrency(product.liquid_cost)}</span>
                          </div>
                          {product.excise_details && (
                            <div className="flex justify-between text-sm text-orange-700 font-medium">
                              <span>Totale Accisa Infusi/Aromi per bottiglia:</span>
                              <span>{formatCurrency(product.excise_details.excise_per_bottle)}</span>
                            </div>
                          )}
                          <div className="text-xs text-blue-600 italic mt-2">
                            * Ricetta originale: {recipe.final_volume.toFixed(1)}L batch = {bottlesFromRecipe.toFixed(1)} bottiglie da {product.bottle_volume_ml}ml
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Componenti Packaging Dettaglio */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">📦 Dettaglio Componenti Packaging:</h4>
                    <div className="grid grid-cols-1 gap-1 text-sm text-gray-600">
                      {product.packaging_components.map((component, index) => (
                        <div key={index} className="flex justify-between bg-gray-50 px-2 py-1 rounded">
                          <span>{component.component_name} ({component.quantity}x):</span>
                          <span className="font-medium">{formatCurrency(component.cost)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {product.notes && (
                    <div className="pt-2 border-t">
                      <h4 className="text-sm font-medium text-gray-900 mb-1">📝 Note:</h4>
                      <p className="text-sm text-gray-600">{product.notes}</p>
                    </div>
                  )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Products;